import React, { useEffect, useState } from "react";
import { Loader2, CalendarDays, Plus, CalendarCheck } from "lucide-react";
import empApi from "../../lib/empApi";

const TYPE = {
  work: { label: "Kerja", cls: "bg-emerald-500/15 text-emerald-300" },
  off: { label: "Libur", cls: "bg-slate-600/30 text-slate-300" },
  holiday: { label: "Hari Libur", cls: "bg-amber-500/15 text-amber-300" },
};
const STATUS = {
  pending: { label: "Menunggu", cls: "bg-amber-500/15 text-amber-300" },
  approved: { label: "Disetujui", cls: "bg-emerald-500/15 text-emerald-300" },
  rejected: { label: "Ditolak", cls: "bg-red-500/15 text-red-300" },
  cancelled: { label: "Dibatalkan", cls: "bg-slate-600/30 text-slate-400" },
};

export default function EmpSchedule() {
  const [sched, setSched] = useState(null);
  const [leave, setLeave] = useState(null);
  const [form, setForm] = useState({ start_date: "", end_date: "", reason: "" });
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const loadLeave = () => empApi.get("/employee/leave").then(({ data }) => setLeave(data)).catch(() => {});
  useEffect(() => {
    empApi.get("/employee/schedule").then(({ data }) => setSched(data)).catch(() => {});
    loadLeave();
  }, []);

  const submit = async () => {
    setErr("");
    if (!form.start_date || !form.end_date) { setErr("Tanggal mulai & selesai wajib diisi"); return; }
    setSubmitting(true);
    try {
      await empApi.post("/employee/leave", form);
      setShow(false); setForm({ start_date: "", end_date: "", reason: "" }); loadLeave();
    } catch (e) { setErr(e?.response?.data?.detail || "Gagal mengajukan"); } finally { setSubmitting(false); }
  };

  const cancelReq = async (id) => {
    if (!window.confirm("Batalkan pengajuan ini?")) return;
    try { await empApi.post(`/employee/leave/${id}/cancel`); loadLeave(); } catch { /* ignore */ }
  };

  if (!sched || !leave) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-amber-400" /></div>;

  return (
    <div className="space-y-4" data-testid="emp-schedule-page">
      <div className="flex items-center gap-2"><CalendarDays size={20} className="text-amber-400" /><h2 className="font-heading text-xl font-bold text-white">Jadwal & Cuti</h2></div>

      {/* Leave balance */}
      <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-indigo-900/40 to-slate-800/40 p-4" data-testid="emp-leave-balance">
        <p className="text-sm font-semibold text-slate-300">Saldo Cuti Bulan Ini</p>
        <div className="mt-2 grid grid-cols-3 gap-3 text-center">
          <div><p className="font-heading text-2xl font-bold text-white">{leave.balance.quota}</p><p className="text-[11px] text-slate-400">Jatah</p></div>
          <div><p className="font-heading text-2xl font-bold text-amber-400">{leave.balance.used}</p><p className="text-[11px] text-slate-400">Terpakai</p></div>
          <div><p className="font-heading text-2xl font-bold text-emerald-400">{leave.balance.remaining}</p><p className="text-[11px] text-slate-400">Sisa</p></div>
        </div>
        <button onClick={() => setShow(true)} data-testid="emp-leave-request-btn"
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 py-2.5 text-sm font-bold text-slate-900">
          <Plus size={16} /> Ajukan Cuti / Izin
        </button>
      </div>

      {/* Weekly schedule */}
      <div className="rounded-2xl border border-slate-800 bg-slate-800/40 p-4" data-testid="emp-schedule-week">
        <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-300"><CalendarCheck size={16} /> 7 Hari Ke Depan</p>
        <div className="space-y-1.5">
          {sched.weekly.map((d) => (
            <div key={d.date} className="flex items-center justify-between border-b border-slate-800 py-1.5 last:border-0" data-testid={`emp-schedule-day-${d.date}`}>
              <span className="text-sm text-slate-300">{d.date}</span>
              <div className="flex items-center gap-2">
                {d.type !== "holiday" && <span className="text-xs text-slate-500">{d.shift_start}–{d.shift_end}</span>}
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${TYPE[d.type]?.cls}`}>{d.label || TYPE[d.type]?.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Leave history */}
      <div className="rounded-2xl border border-slate-800 bg-slate-800/40 p-4" data-testid="emp-leave-history">
        <p className="mb-2 text-sm font-semibold text-slate-300">Riwayat Pengajuan</p>
        {leave.requests.length === 0 ? <p className="py-2 text-center text-xs text-slate-500">Belum ada pengajuan</p> : (
          <div className="space-y-2">
            {leave.requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg bg-slate-900/50 px-3 py-2" data-testid={`emp-leave-req-${r.id}`}>
                <div><p className="text-sm text-white">{r.start_date} → {r.end_date}</p><p className="text-[11px] text-slate-500">{r.reason || "—"} · {r.days} hari</p></div>
                <div className="flex items-center gap-2">
                  {r.status === "pending" && (
                    <button onClick={() => cancelReq(r.id)} data-testid={`emp-leave-cancel-${r.id}`}
                      className="rounded-full border border-slate-600 px-2 py-0.5 text-[11px] font-semibold text-slate-300 active:scale-95">Batalkan</button>
                  )}
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS[r.status]?.cls}`}>{STATUS[r.status]?.label}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Request modal */}
      {show && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center" onClick={() => setShow(false)}>
          <div className="w-full max-w-md rounded-t-2xl border border-slate-700 bg-slate-900 p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid="emp-leave-modal">
            <h3 className="mb-3 font-heading text-lg font-bold text-white">Ajukan Cuti / Izin</h3>
            <div className="space-y-3">
              <div><label className="text-xs text-slate-400">Tanggal Mulai</label><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white" data-testid="emp-leave-start" /></div>
              <div><label className="text-xs text-slate-400">Tanggal Selesai</label><input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white" data-testid="emp-leave-end" /></div>
              <div><label className="text-xs text-slate-400">Alasan</label><textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white" rows={2} data-testid="emp-leave-reason" /></div>
              {err && <p className="text-xs text-red-400" data-testid="emp-leave-error">{err}</p>}
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setShow(false)} className="flex-1 rounded-xl border border-slate-700 py-2.5 text-sm font-semibold text-slate-300">Batal</button>
              <button onClick={submit} disabled={submitting} className="flex-1 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 py-2.5 text-sm font-bold text-slate-900 disabled:opacity-60" data-testid="emp-leave-submit">{submitting ? "Mengirim..." : "Kirim"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
